import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { useCurrentLanguage } from '~/components/Translation';
import { API_PATH } from '~/constants';
import analytics from '~/services/analytics';
import googleTagManager from '~/services/googleTagManager';
import { localeSelectors } from '~/services/locale';
import request from '~/services/request';
import { signupSelectors } from '~/services/signup';
import { userSelectors } from '~/services/user';
import { useLegal } from '~/services/user/user.hooks';
import getCometlyParams from '~/services/utils/cometly';
import getOneTrustConsentData from '~/services/utils/oneTrustConsent';

const useGoogleAuth = ({ action, redirectTo }: Props) => {
	const [apiError, setApiError] = useState<string>();
	const { privacyNotice, termsOfService } = useLegal();
	const isAuth = useSelector(userSelectors.isAuth);

	const affiliateTrackingId = useSelector(
		signupSelectors.getAffiliateTrackingId,
	);
	const currency = useSelector(localeSelectors.getCurrency);
	const language = useCurrentLanguage();
	const partner = useSelector(signupSelectors.getPartner);
	const referrer = useSelector(signupSelectors.getReferrer);
	const referrerType = useSelector(signupSelectors.getReferrerType);
	const broadcastGuestSignup = useSelector(
		signupSelectors.broadcastGuestSignup,
	);
	const wasBroadcastGuest = useSelector(signupSelectors.wasBroadcastGuest);
	const { cometlyFingerprint, cometlyToken } = getCometlyParams();
	const cookieConsentStatus = getOneTrustConsentData();

	const onSubmit = useCallback(
		async ({
			code,
			token,
			onetapSignUp,
		}: {
			code?: string;
			token?: string;
			onetapSignUp: boolean;
		}) => {
			if (isAuth) {
				return;
			}

			const requestUrl = (() => {
				if (action === 'login') return '/login';
				if (action === 'signup') return '/signup';
				return window.location.pathname;
			})();

			request<Response>({
				method: 'POST',
				uri: `${API_PATH}/user/google_auth`,
				// Long timeout on signup. May require several third party api requests and extra
				// database writes (affiliates, partners, referrals, sendgrid, stripe, etc.).
				timeout: 25000,
				body: {
					action,
					code,
					token,
					...(cometlyFingerprint && { cometlyFingerprint }),
					...(cometlyToken && { cometlyToken }),
					currency,
					requestLocationType: 'google',
					requestUrl,
					...(affiliateTrackingId && { affiliateTrackingId }),
					language,
					...(partner && { partner }),
					...(referrer && { referrer }),
					...(referrerType && { referrerType }),
					...(broadcastGuestSignup && { broadcastGuestSignup }),
					...(wasBroadcastGuest && { wasBroadcastGuest }),
					privacyNoticeVersion: privacyNotice.version,
					termsOfServiceVersion: termsOfService.version,
					...(cookieConsentStatus && {
						cookieConsentAnalytics: cookieConsentStatus.analytics,
						cookieConsentProfiling: cookieConsentStatus.profiling,
					}),
				},
			})
				.then(response => {
					if (
						'userId' in response &&
						'teamId' in response &&
						'workspaceId' in response
					) {
						const isSignUp = onetapSignUp || response.action === 'signup';

						if (isSignUp) {
							googleTagManager.event('signup_complete', {
								method: 'google',
								userId: response.userId ?? '',
								teamId: response.teamId ?? '',
								workspaceId: response.workspaceId ?? '',
							});
						}

						analytics.track(analytics.events.COOKIES_CONSENT_UPDATED, {
							analytics: cookieConsentStatus?.analytics,
							profiling: cookieConsentStatus?.profiling,
						});

						analytics.trackSignIn('google', isSignUp, {
							userId: response.userId ?? '',
							teamId: response.teamId ?? '',
						});
					}

					if ('redirectUrl' in response) {
						window.location.href = response.redirectUrl;
						return;
					}

					window.location.href =
						redirectTo ||
						(response.action === 'login' ? '/broadcasts' : '/welcome');
				})
				.catch(error => {
					if (error.statusCode === 404) {
						setApiError('userNotFound');
						return;
					}
					setApiError('google');
				});
		},
		[
			action,
			affiliateTrackingId,
			broadcastGuestSignup,
			cometlyFingerprint,
			cometlyToken,
			cookieConsentStatus,
			currency,
			isAuth,
			language,
			partner,
			privacyNotice.version,
			redirectTo,
			referrer,
			referrerType,
			termsOfService.version,
			wasBroadcastGuest,
		],
	);

	return {
		onSubmit,
		isAuth,
		apiError,
	};
};

type Action = 'login' | 'signup' | 'onetap';

interface Props {
	action?: Action;
	redirectTo?: string;
}

type ResponseSSO = {
	redirectUrl: string;
};

type ResponseSuccess = {
	userId?: string;
	teamId?: string;
	workspaceId?: string;
	action?: Action;
};

type Response = ResponseSSO | ResponseSuccess;

export default useGoogleAuth;
